import { LessonStatus, isLessonStatus } from '../Models/enums';
import type { LessonModel, LessonVisualState } from '../Models/LessonModel';
import type { StudentSlotModel } from '../Models/StudentModel';

/**
 * Debt is a function of time.
 *
 * This mirrors Backend/src/2-utils/date-util.ts deliberately: the client
 * decides what to draw before a round trip, and the server decides what is
 * true. The two must agree, so they are written the same way and tested
 * against the same cases. Changing one without the other is the bug.
 */

export const DEFAULT_LESSON_MINUTES = 60;

/** YYYY-MM-DD in the tutor's own timezone. Never toISOString, which is UTC. */
export function localDateString(at: Date = new Date()): string {
  const year = at.getFullYear();
  const month = String(at.getMonth() + 1).padStart(2, '0');
  const day = String(at.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function weekdayOf(date: string): number {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1).getDay();
}

export function minutesOfDay(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

export function lessonStatusOf(lesson: Pick<LessonModel, 'status' | 'paid'>): LessonStatus {
  if (isLessonStatus(lesson.status)) return lesson.status;
  return Number(lesson.paid) === 1 ? LessonStatus.Paid : LessonStatus.Unpaid;
}

export interface LessonSchedule {
  time: string | null;
  durationMinutes: number;
}

/**
 * When a lesson runs and for how long. Its own time wins; otherwise it
 * inherits the student's recurring slot for that weekday, which is where the
 * duration comes from either way.
 */
export function lessonSchedule(
  lesson: Pick<LessonModel, 'date' | 'time'>,
  slots: readonly StudentSlotModel[] = []
): LessonSchedule {
  const weekday = weekdayOf(lesson.date);
  const daySlots = slots.filter(slot => Number(slot.weekday) === weekday);
  const match = lesson.time
    ? daySlots.find(slot => slot.time === lesson.time)
    : daySlots[0];
  return {
    time: lesson.time ?? match?.time ?? null,
    durationMinutes: Number(match?.durationMinutes) || DEFAULT_LESSON_MINUTES
  };
}

/**
 * A lesson is delivered once its end time has passed: a 45-minute lesson at
 * 15:00 has happened at 15:45, not at midnight. With only a day and no slot to
 * inherit an hour from, it counts once the day is over, which errs towards
 * "not yet owed" on purpose.
 */
export function lessonHasHappened(
  lesson: Pick<LessonModel, 'date' | 'time'>,
  slots: readonly StudentSlotModel[] = [],
  now: Date = new Date()
): boolean {
  const todayKey = localDateString(now);
  if (lesson.date < todayKey) return true;
  if (lesson.date > todayKey) return false;
  const { time, durationMinutes } = lessonSchedule(lesson, slots);
  if (!time) return false;
  return now.getHours() * 60 + now.getMinutes() >= minutesOfDay(time) + durationMinutes;
}

/** A charge not yet collected and already due. Never `!paid`. */
export function isOwed(
  lesson: Pick<LessonModel, 'date' | 'time' | 'status' | 'paid'>,
  slots: readonly StudentSlotModel[] = [],
  now: Date = new Date()
): boolean {
  const status = lessonStatusOf(lesson);
  if (status === LessonStatus.CancelledCharged) return true;
  if (status === LessonStatus.Unpaid) return lessonHasHappened(lesson, slots, now);
  return false;
}

/** An unpaid lesson that has not been given yet: potential income, not debt. */
export function isScheduledLesson(
  lesson: Pick<LessonModel, 'date' | 'time' | 'status' | 'paid'>,
  slots: readonly StudentSlotModel[] = [],
  now: Date = new Date()
): boolean {
  return lessonStatusOf(lesson) === LessonStatus.Unpaid && !lessonHasHappened(lesson, slots, now);
}

export function isCollected(lesson: Pick<LessonModel, 'status' | 'paid'>): boolean {
  return lessonStatusOf(lesson) === LessonStatus.Paid;
}

export function isFreeCancel(lesson: Pick<LessonModel, 'status' | 'paid'>): boolean {
  return lessonStatusOf(lesson) === LessonStatus.CancelledFree;
}

/**
 * A lesson that was called off, whether or not it is still charged. It stays
 * in the ledger because the money question is still real, but it is not an
 * appointment any more, so no calendar should draw it.
 */
export function isCancelled(lesson: Pick<LessonModel, 'status' | 'paid'>): boolean {
  const status = lessonStatusOf(lesson);
  return status === LessonStatus.CancelledCharged || status === LessonStatus.CancelledFree;
}

/** Everything carrying real money, as opposed to a free cancellation. */
export function isChargeable(lesson: Pick<LessonModel, 'status' | 'paid'>): boolean {
  return !isFreeCancel(lesson);
}

/** The CSS state class for a lesson row, chip or calendar block. */
export function lessonVisualState(
  lesson: Pick<LessonModel, 'date' | 'time' | 'status' | 'paid'>,
  slots: readonly StudentSlotModel[] = [],
  now: Date = new Date()
): LessonVisualState {
  if (isScheduledLesson(lesson, slots, now)) return 'is-scheduled';
  switch (lessonStatusOf(lesson)) {
    case LessonStatus.Paid: return 'is-paid';
    case LessonStatus.CancelledCharged: return 'is-cancelled-charged';
    case LessonStatus.CancelledFree: return 'is-cancelled-free';
    default: return 'is-unpaid';
  }
}
